import React, { cloneElement, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useGateReveal } from '../hooks/useGateReveal';
import { getGateHighlight, getGateRevealAppearance, resolveGateRevealEffect } from '../utils/gatePresentation';
import GateRevealCelebration from './GateRevealCelebration';
import { getUnlockSoundEnabled, setUnlockSoundEnabled, SOUND_EVENT, playUnlockChime, prefersReducedMotion } from '../utils/unlockCelebration';
import GateBadge from './GateBadge';
import './GateRevealPanel.css';

// Retains the original panel root, style and position; no extra layout wrapper.
export default function GateRevealPanel({ children, hasAccess, isLoading, access = {}, title, panelStyle = {} }) {
    const { needsReveal, reveal } = useGateReveal({ hasAccess, isLoading, ...access });
    const [soundEnabled, setSoundEnabled] = useState(getUnlockSoundEnabled);
    const [burst, setBurst] = useState(null);
    const focusTarget = useRef(null);
    const highlight = getGateHighlight(panelStyle);
    const appearance = getGateRevealAppearance(panelStyle);
    useEffect(() => {
        const sync = () => setSoundEnabled(getUnlockSoundEnabled());
        window.addEventListener(SOUND_EVENT, sync);
        window.addEventListener('storage', sync);
        return () => { window.removeEventListener(SOUND_EVENT, sync); window.removeEventListener('storage', sync); };
    }, []);
    useEffect(() => {
        if (!burst) return undefined;
        const timer = setTimeout(() => setBurst(null), 1200);
        return () => clearTimeout(timer);
    }, [burst]);
    useLayoutEffect(() => {
        if (needsReveal || isLoading || !hasAccess || !focusTarget.current) return;
        const root = focusTarget.current;
        focusTarget.current = null;
        const heading = root.querySelector('h1, h2, h3, [role="heading"]');
        const target = heading || root;
        if (!target.hasAttribute('tabindex')) {
            target.setAttribute('tabindex', '-1');
            target.addEventListener('blur', () => target.removeAttribute('tabindex'), { once: true });
        }
        target.focus({ preventScroll: true });
    }, [needsReveal, isLoading, hasAccess]);
    const handleReveal = event => {
        event.preventDefault();
        event.stopPropagation();
        if (!reveal()) return;
        const root = event.currentTarget.closest('[data-gate-reveal-root]');
        focusTarget.current = root;
        if (root && !prefersReducedMotion()) {
            const rect = root.getBoundingClientRect();
            setBurst({ scope: access.revealScope, effect: resolveGateRevealEffect(appearance.effect), color: highlight.color,
                left: rect.left, top: rect.top, width: rect.width, height: rect.height });
        }
        playUnlockChime();
    };
    if (!hasAccess || isLoading || !access.requiresReveal) return children;
    if (needsReveal) {
        return cloneElement(children, { 'data-gate-reveal-root': true },
            <div className={`gate-reveal-prompt${highlight.enabled ? ` gate-highlight-${highlight.mode}` : ''}`}
                style={{ '--gate-highlight-color': highlight.color, '--gate-highlight-duration': `${highlight.duration}s`,
                    '--gate-reveal-panel-color': appearance.panelColor, '--gate-reveal-text-color': appearance.textColor,
                    '--gate-reveal-font': appearance.fontFamily }}>
                <button type="button" className="gate-reveal-button" onClick={handleReveal}>
                    <span className="gate-reveal-reason">You have {access.accessReason || 'access'}</span>
                    <span className="gate-reveal-action">Click to view {title || 'this content'}</span>
                </button>
                <button type="button" className="gate-reveal-sound" aria-pressed={!soundEnabled}
                    onClick={() => setUnlockSoundEnabled(!soundEnabled)}>Mute reveal sound</button>
                {access.resetPasswordAccess && <GateBadge gateNamesLabel="password" onResetPasswordAccess={access.resetPasswordAccess}
                    resetPasswordError={access.resetPasswordError} />}
            </div>);
    }
    return cloneElement(children, { 'data-gate-reveal-root': true }, <>{children.props.children}
        {burst?.scope === access.revealScope && <GateRevealCelebration burst={burst} />}</>);
}
